import { apiClient } from './client';

export interface FinanceItem {
  entityId: string;
  channelId: string | null;
  entityName: string;
  category: string;
  type: 'EXPENSE' | 'REVENUE';
  value: number;
}

export interface FinanceData {
  totalExpenses: number;
  totalRevenues: number;
  total: number;
  currency?: string;
  breakdown: FinanceItem[];
  unfilteredFinance?: FinanceData;
  specs?: any[];
  transactions?: any[];
  metrics?: Record<string, any>;
  dailyBreakdown?: Record<string, { metrics: Record<string, any>; breakdown: any[] }>;
  detailedBreakdown?: FinanceItem[];
  trafficSummary?: {
    totalRevenue: number;
    organicRevenue: number;
    paidRevenue: number;
    sources: Record<string, { total: number; paid: number; organic: number; count: number }>;
  };
  verdict?: {
    heuristic: any | null;
    llm: string | null;
    lastAnalyzed: string | null;
  };
}

export interface BoardChannel {
  id: string;
  boardId: string;
  channelId: string;
  order: number;
  isActive: boolean;
  settings?: string;
  createdAt: string;
  updatedAt: string;
  channel: {
    id: string;
    name: string;
    channelType: string;
    status: string;
    lastSyncAt?: string;
  };
}

export interface BoardCheckedEntity {
  id: string;
  boardId: string;
  entityId: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  entity: {
    id: string;
    name?: string;
    entityType: string;
    platform: string;
    currency?: string;
  };
}

export interface CurrencyObject {
  default: string;
  conversions: Array<{
    currency: string;
    rate: number;
    manual: boolean;
    originalRate?: number;
  }>;
}

export interface Board {
  id: string;
  name: string;
  description?: string;
  currency?: CurrencyObject;
  startDate?: string;
  endDate?: string;
  chartConfig?: any[];
  createdAt: string;
  updatedAt: string;
  stats?: {
    channelCount: number;
    entityCount: number;
  };
  pages?: BoardPage[];
}

export interface BoardPage {
  id: string;
  name: string;
  type?: string;
  order: number;
  chartConfig: any[];
  boardId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface BoardJournal {
  id: string;
  name: string;
  type: 'system' | 'custom';
  isEnabled: boolean;
  order: number;
}

export interface BoardFull extends Board {
  boardChannels?: BoardChannel[];
  checkedEntities?: BoardCheckedEntity[];
}

export interface CreateBoardRequest {
  name: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  chartConfig?: any[];
  createDefaultPage?: boolean;
}

export interface UpdateBoardRequest {
  name?: string;
  description?: string;
  currency?: string;
  exchangeRates?: { [key: string]: { rate: number; manual: boolean } };
  startDate?: string;
  endDate?: string;
  chartConfig?: any[];
}

export interface BoardResponse {
  success: boolean;
  message: string;
  data: Board;
}

export interface BoardsResponse {
  success: boolean;
  message: string;
  data: Board[];
}

export interface BoardSummaryResponse {
  success: boolean;
  message: string;
  data: {
    board: Board;
    connections: number;
    entities: number;
    transactions: number;
  };
}

class BoardService {
  async getBoards(): Promise<BoardsResponse> {
    const response = await apiClient.get('/boards');
    return response.data;
  }

  async getBoard(id: string): Promise<BoardResponse> {
    const response = await apiClient.get(`/boards/${id}`);
    return response.data;
  }

  async createBoard(data: CreateBoardRequest): Promise<BoardResponse> {
    const response = await apiClient.post('/boards', data);
    return response.data;
  }

  async updateBoard(id: string, data: UpdateBoardRequest): Promise<BoardResponse> {
    const response = await apiClient.put(`/boards/${id}`, data);
    return response.data;
  }

  async deleteBoard(id: string): Promise<void> {
    await apiClient.delete(`/boards/${id}`);
  }

  // Save board channels and checked entities
  async saveBoardChannels(boardId: string, channelIds: string[], checkedEntityIds: string[], full: boolean = false): Promise<any> {
    const url = full ? `/boards/${boardId}/channels?full=true` : `/boards/${boardId}/channels`;
    const response = await apiClient.post(url, {
      channelIds,
      checkedEntityIds
    });
    return response.data;
  }

  /**
   * Toggle a connection on/off. The path parameter is the BoardChannel row ID
   * (not the underlying real channel ID), which lets a single pseudo-channel
   * be toggled without affecting siblings sharing the same real channel.
   */
  async updateBoardChannelStatus(boardId: string, boardChannelId: string, isActive: boolean): Promise<any> {
    const response = await apiClient.patch(`/boards/${boardId}/channels/${boardChannelId}/status`, { isActive });
    return response.data;
  }

  /** Attach a pseudo-channel (one root entity of a pseudo-eligible channel) to a board. */
  async addPseudoChannelToBoard(
    boardId: string,
    realChannelId: string,
    sourceEntityId: string,
  ): Promise<any> {
    const response = await apiClient.post(`/boards/${boardId}/pseudo-channels`, {
      realChannelId,
      sourceEntityId,
    });
    return response.data;
  }

  /** Detach a pseudo-channel from a board. */
  async removePseudoChannelFromBoard(boardId: string, boardChannelId: string): Promise<any> {
    const response = await apiClient.delete(`/boards/${boardId}/pseudo-channels/${boardChannelId}`);
    return response.data;
  }

  async updateBoardJournalStatus(boardId: string, journalId: string, isActive: boolean): Promise<any> {
    const response = await apiClient.patch(`/boards/${boardId}/journals/${journalId}/status`, { isActive });
    return response.data;
  }

  async updateBoardEntityStatus(boardId: string, entityId: string, isActive: boolean): Promise<any> {
    const response = await apiClient.patch(`/boards/${boardId}/entities/${entityId}/status`, { isActive });
    return response.data;
  }

  async getBoardChannels(boardId: string): Promise<{ success: boolean; data: { channelIds: string[], checkedEntityIds: string[] } }> {
    const response = await apiClient.get(`/boards/${boardId}/channels`);
    return response.data;
  }


  async getBoardPayload(id: string, groups?: string[]): Promise<any> {
    const url = groups?.length ? `/boards/${id}/payload?groups=${groups.join(',')}` : `/boards/${id}/payload`;
    const response = await apiClient.get(url);
    return response.data;
  }

  async updateEntitySelection(id: string, selectedEntityIds: string[]): Promise<any> {
    const response = await apiClient.patch(`/boards/${id}/entities/selection`, {
      selectedEntityIds
    });
    return response.data;
  }

  async updateBoardChannels(id: string, channelIds: string[]): Promise<any> {
    const response = await apiClient.patch(`/boards/${id}/channels`, {
      channelIds
    });
    return response.data;
  }

  async updateOperationSelection(id: string, selectedOperationIds: string[]): Promise<any> {
    const response = await apiClient.patch(`/boards/${id}/operations/selection`, {
      selectedOperationIds
    });
    return response.data;
  }

  async updateBoardChannelsAndSelections(
    id: string,
    data: { channelIds: string[]; selectedEntityIds: string[] }
  ): Promise<any> {
    // Use the saveBoardChannels endpoint with full=false to get only structure (optimization)
    return this.saveBoardChannels(id, data.channelIds, data.selectedEntityIds, false);
  }

  async getCurrencyPreview(
    boardId: string,
    targetCurrency: string,
    manualRates?: { [key: string]: number }
  ): Promise<CurrencyObject> {
    const params = new URLSearchParams();
    params.append('targetCurrency', targetCurrency);
    if (manualRates) {
      params.append('manualRates', JSON.stringify(manualRates));
    }

    const response = await apiClient.get(`/currency/board/${boardId}/preview?${params}`);
    return response.data.data;
  }

  // Page Management
  async createPage(boardId: string, data: { name: string; type?: string }): Promise<BoardPage> {
    const response = await apiClient.post(`/boards/${boardId}/pages`, data);
    return response.data.data;
  }

  async updatePage(boardId: string, pageId: string, data: Partial<BoardPage>): Promise<BoardPage> {
    const response = await apiClient.put(`/boards/${boardId}/pages/${pageId}`, data);
    return response.data.data;
  }

  async deletePage(boardId: string, pageId: string): Promise<void> {
    await apiClient.delete(`/boards/${boardId}/pages/${pageId}`);
  }

  async reorderPages(boardId: string, pageIds: string[]): Promise<void> {
    await apiClient.patch(`/boards/${boardId}/pages/reorder`, { pageIds });
  }

  // Journal Management
  async createJournal(boardId: string, data: { name: string; importType?: string }): Promise<BoardJournal> {
    const response = await apiClient.post(`/boards/${boardId}/journals`, data);
    return response.data.data;
  }

  async updateJournal(boardId: string, journalId: string, data: { name?: string; isEnabled?: boolean }): Promise<BoardJournal> {
    const response = await apiClient.put(`/boards/${boardId}/journals/${journalId}`, data);
    return response.data.data;
  }

  async deleteJournal(boardId: string, journalId: string): Promise<void> {
    await apiClient.delete(`/boards/${boardId}/journals/${journalId}`);
  }

  async createJournalTransaction(boardId: string, journalId: string, data: any): Promise<any> {
    const response = await apiClient.post(`/boards/${boardId}/journals/${journalId}/operations`, data);
    return response.data.data;
  }

  async getJournalOperations(boardId: string, journalId: string): Promise<any[]> {
    const response = await apiClient.get(`/boards/${boardId}/journals/${journalId}/operations`);
    return response.data.data;
  }

  async updateJournalOperation(boardId: string, journalId: string, operationId: string, data: any): Promise<any> {
    const response = await apiClient.put(`/boards/${boardId}/journals/${journalId}/operations/${operationId}`, data);
    return response.data.data;
  }

  async deleteJournalOperation(boardId: string, journalId: string, operationId: string): Promise<void> {
    await apiClient.delete(`/boards/${boardId}/journals/${journalId}/operations/${operationId}`);
  }

  async getOperationFileUrl(journalId: string, operationId: string, fileKey?: string): Promise<{ url: string }> {
    const url = fileKey ? `/journals/${journalId}/operations/${operationId}/file?fileKey=${encodeURIComponent(fileKey)}` : `/journals/${journalId}/operations/${operationId}/file`;
    const response = await apiClient.get(url, { responseType: 'blob' });
    return response.data;
  }

  async getOperationFiles(journalId: string, operationId: string): Promise<any[]> {
    const response = await apiClient.get(`/journals/${journalId}/operations/${operationId}/files`);
    return response.data.data;
  }

  async addOperationFile(journalId: string, operationId: string, file: File): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`/journals/${journalId}/operations/${operationId}/files`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.data;
  }

  async deleteOperationFile(journalId: string, operationId: string, fileKey: string): Promise<void> {
    await apiClient.delete(`/journals/${journalId}/operations/${operationId}/files/${encodeURIComponent(fileKey)}`);
  }
  // Global Journal Management
  async getGlobalJournals(): Promise<any> {
    const response = await apiClient.get('/journals');
    return response.data;
  }

  async createGlobalJournal(data: { name: string }): Promise<any> {
    const response = await apiClient.post('/journals', data);
    return response.data.data;
  }

  async updateGlobalJournal(id: string, data: { name?: string }): Promise<any> {
    const response = await apiClient.put(`/journals/${id}`, data);
    return response.data.data;
  }

  async deleteGlobalJournal(id: string): Promise<void> {
    await apiClient.delete(`/journals/${id}`);
  }

  async getGlobalJournalOperations(id: string): Promise<any[]> {
    const response = await apiClient.get(`/journals/${id}/operations`);
    return response.data.data;
  }

  async createGlobalJournalOperation(id: string, data: any): Promise<any> {
    const response = await apiClient.post(`/journals/${id}/operations`, data);
    return response.data.data;
  }

  async updateGlobalJournalOperation(journalId: string, operationId: string, data: any): Promise<any> {
    const response = await apiClient.put(`/journals/${journalId}/operations/${operationId}`, data);
    return response.data.data;
  }

  async deleteGlobalJournalOperation(journalId: string, operationId: string): Promise<void> {
    await apiClient.delete(`/journals/${journalId}/operations/${operationId}`);
  }

  async exportJournalOperations(journalId: string, operationIds: string[], format: 'csv-excel' | 'csv-plain'): Promise<Blob> {
    const response = await apiClient.post(`/journals/${journalId}/operations/export`,
      { operationIds, format },
      { responseType: 'blob' }
    );
    return response.data;
  }

  // Board-Journal Linking
  async linkJournalToBoard(boardId: string, journalId: string): Promise<any> {
    const response = await apiClient.post(`/boards/${boardId}/journals/link`, { journalId });
    return response.data.data;
  }

  async unlinkJournalFromBoard(boardId: string, journalId: string): Promise<void> {
    await apiClient.post(`/boards/${boardId}/journals/${journalId}/unlink`);
  }

  // ==================== SHARING API ====================

  async createShare(journalId: string, permission: 'read' | 'readwrite', emails: string[] = []): Promise<any> {
    const response = await apiClient.post(`/journals/${journalId}/share`, { permission, emails });
    return response.data.data;
  }

  async updateJournalShares(journalId: string, shares: Array<{ email: string; permission: string }>): Promise<any> {
    const response = await apiClient.post(`/journals/${journalId}/shares`, { shares });
    return response.data;
  }

  async getShares(journalId: string): Promise<any[]> {
    const response = await apiClient.get(`/journals/${journalId}/shares`);
    return response.data.data;
  }

  async revokeShare(journalId: string, shareId: string): Promise<void> {
    await apiClient.delete(`/journals/${journalId}/shares/${shareId}`);
  }

  async verifyShareToken(token: string): Promise<any> {
    const response = await apiClient.get(`/journals/share/verify/${token}`);
    return response.data.data;
  }

  async acceptShare(token: string): Promise<any> {
    const response = await apiClient.post(`/journals/share/accept`, { token });
    return response.data.data;
  }

  async getSharedJournals(): Promise<any[]> {
    const response = await apiClient.get(`/journals/shared`);
    return response.data.data;
  }

  async removeSharedAccess(journalId: string): Promise<void> {
    await apiClient.delete(`/journals/shared/${journalId}`);
  }

  // Board Spec Management
  async getBoardSpecs(boardId: string): Promise<any> {
    const response = await apiClient.get(`/boards/${boardId}/specs`);
    return response.data;
  }

  async createBoardSpec(boardId: string, data: { name: string; formula: string; type: 'REVENUE' | 'EXPENSE' }): Promise<any> {
    const response = await apiClient.post(`/boards/${boardId}/specs`, data);
    return response.data;
  }

  async updateBoardSpec(boardId: string, specId: string, data: { name?: string; formula?: string; type?: 'REVENUE' | 'EXPENSE' }): Promise<any> {
    const response = await apiClient.put(`/boards/${boardId}/specs/${specId}`, data);
    return response.data;
  }

  async deleteBoardSpec(boardId: string, specId: string): Promise<any> {
    const response = await apiClient.delete(`/boards/${boardId}/specs/${specId}`);
    return response.data;
  }


}

export const boardService = new BoardService();

// Helper function to generate unique board name
export const generateUniqueBoardName = (existingBoards: Board[]): string => {
  const untitledNames = existingBoards
    .map(w => w.name)
    .filter(name => name.match(/^untitled( \d+)?$/i));

  if (untitledNames.length === 0) return "untitled";

  const numbers = untitledNames
    .map(name => {
      const match = name.match(/^untitled(?: (\d+))?$/i);
      return match ? (match[1] ? parseInt(match[1]) : 0) : -1;
    })
    .filter(n => n >= 0);

  const maxNumber = Math.max(...numbers);
  return maxNumber === 0 ? "untitled 1" : `untitled ${maxNumber + 1}`;
};
